import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { contractorService } from '../../../services/contractorService';

const EditContractorModal = ({ isOpen, onClose, onSuccess, contractor }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    role: 'general_contractor',
    hourlyRate: '',
    location: '',
    availabilityStatus: 'available'
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (contractor) {
      setFormData({
        name: contractor?.name || '',
        email: contractor?.email || '',
        phone: contractor?.phone || '',
        role: contractor?.role || 'general_contractor',
        hourlyRate: contractor?.hourlyRate ? contractor?.hourlyRate?.toString() : '',
        location: contractor?.location || '',
        availabilityStatus: contractor?.availabilityStatus || 'available'
      });
    }
  }, [contractor]);

  const handleChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setError('');
    setLoading(true);

    try {
      await contractorService?.update(contractor?.id, {
        ...formData,
        hourlyRate: formData?.hourlyRate ? parseFloat(formData?.hourlyRate) : null
      });
      
      onSuccess?.();
      onClose();
    } catch (err) {
      setError(err?.message || 'Failed to update contractor');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[200] p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900">Edit Contractor</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            disabled={loading}
          >
            <Icon name="X" size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
              {error}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Name <span className="text-red-500">*</span>
              </label>
              <Input
                type="text"
                name="name"
                value={formData?.name}
                onChange={handleChange}
                required
                placeholder="Enter contractor name"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email <span className="text-red-500">*</span>
              </label>
              <Input
                type="email"
                name="email"
                value={formData?.email}
                onChange={handleChange}
                required
                placeholder="contractor@email.com"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Phone
              </label>
              <Input
                type="tel"
                name="phone"
                value={formData?.phone}
                onChange={handleChange}
                placeholder="(555) 123-4567"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Specialty/Role <span className="text-red-500">*</span>
              </label>
              <Select
                name="role"
                value={formData?.role}
                onChange={handleChange}
                required
              >
                <option value="General Contractor">General Contractor</option>
                <option value="Electrician">Electrician</option>
                <option value="Plumber">Plumber</option>
                <option value="HVAC Specialist">HVAC Specialist</option>
                <option value="Foundation Specialist">Foundation Specialist</option>
                <option value="Framing Specialist">Framing Specialist</option>
                <option value="Carpenter">Carpenter</option>
                <option value="Roofer">Roofer</option>
                <option value="Painter">Painter</option>
                <option value="Landscaper">Landscaper</option>
              </Select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Hourly Rate
              </label>
              <Input
                type="number"
                name="hourlyRate"
                value={formData?.hourlyRate}
                onChange={handleChange}
                placeholder="75"
                min="0"
                step="5"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Location
              </label>
              <Input
                type="text"
                name="location"
                value={formData?.location}
                onChange={handleChange}
                placeholder="City, State"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Availability Status
              </label>
              <Select
                name="availabilityStatus"
                value={formData?.availabilityStatus}
                onChange={handleChange}
              >
                <option value="available">Available</option>
                <option value="busy">Busy</option>
                <option value="on_leave">On Leave</option>
                <option value="unavailable">Unavailable</option>
              </Select>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading}
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <Icon name="Loader2" size={16} className="animate-spin" />
                  Updating...
                </span>
              ) : (
                'Update Contractor'
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditContractorModal;